import React, { useState, useEffect } from 'react';
import { Calendar, dateFnsLocalizer } from 'react-big-calendar';
import { format, parse, startOfWeek, getDay } from 'date-fns';
import enUS from 'date-fns/locale/en-US';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import {
  Box, Paper, Typography, Chip, CircularProgress, Alert,
  Card, CardContent, Divider, Avatar, Grid
} from '@mui/material';
import ScheduleIcon from '@mui/icons-material/Schedule';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import ClassIcon from '@mui/icons-material/Class';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import { teacherAPI } from '@services/api';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';

const locales = { 'en-US': enUS };
const localizer = dateFnsLocalizer({ format, parse, startOfWeek, getDay, locales });

const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

const TYPE_COLORS = {
  event: '#1976d2',
  sports: '#16a34a',
  cultural: '#7c3aed',
  meeting: '#0891b2',
  other: '#6b7280',
  holiday: '#ef4444',
  exam: '#f59e0b',
};

const PERIOD_COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4', '#f97316', '#14b8a6'];

export default function MySchedule() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [calDate, setCalDate] = useState(new Date());
  const [view, setView] = useState('month');

  useEffect(() => {
    teacherAPI.getMySchedule()
      .then(res => setData(res.data))
      .catch(() => setError('Failed to load schedule'))
      .finally(() => setLoading(false));
  }, []);

  const calEvents = data?.calendar_events
    ? data.calendar_events.map(e => ({
        id: e.id,
        title: e.title,
        start: new Date(e.event_date + 'T00:00:00'),
        end: e.end_date ? new Date(e.end_date + 'T23:59:59') : new Date(e.event_date + 'T23:59:59'),
        color: e.color || TYPE_COLORS[e.event_type] || '#1976d2',
        resource: e,
      }))
    : [];

  const eventStyleGetter = (event) => ({
    style: {
      backgroundColor: event.color,
      borderRadius: '4px',
      border: 'none',
      color: '#fff',
      fontSize: '0.75rem',
      padding: '2px 6px',
    },
  });

  const todaySlots = data?.today_slots || [];
  const weekTimetable = data?.timetable || [];

  const today = new Date();
  const jsDay = today.getDay();
  const todayDayIdx = jsDay === 0 ? 0 : jsDay - 1;

  if (loading) return (
    <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>
  );

  return (
      <Box sx={{ maxWidth: 1200, mx: 'auto', p: { xs: 2, md: 3 } }}>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <Grid container spacing={3}>
          {/* Today's Classes */}
          <Grid item xs={12} md={4}>
            <Paper sx={{ borderRadius: 2, overflow: 'hidden', height: '100%' }}>
              <Box sx={{ p: 2, bgcolor: '#1e293b', color: '#fff', display: 'flex', alignItems: 'center', gap: 1 }}>
                <ScheduleIcon />
                <Typography fontWeight={700}>Today's Classes</Typography>
                <Chip label={DAYS[todayDayIdx] || 'Today'} size="small" sx={{ ml: 'auto', bgcolor: 'rgba(255,255,255,0.15)', color: '#fff' }} />
              </Box>

              {todaySlots.length === 0 ? (
                <Box sx={{ p: 4, textAlign: 'center' }}>
                  <ClassIcon sx={{ fontSize: 48, color: 'grey.300', mb: 1 }} />
                  <Typography color="text.secondary" variant="body2">No classes scheduled today</Typography>
                </Box>
              ) : (
                <Box sx={{ p: 1 }}>
                  {todaySlots.sort((a, b) => a.period_number - b.period_number).map((slot, i) => (
                    <Card key={slot.id} variant="outlined" sx={{ mb: 1, borderLeft: `4px solid ${PERIOD_COLORS[i % PERIOD_COLORS.length]}` }}>
                      <CardContent sx={{ p: '12px !important' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Avatar sx={{ width: 28, height: 28, fontSize: '0.7rem', bgcolor: PERIOD_COLORS[i % PERIOD_COLORS.length] }}>
                            P{slot.period_number}
                          </Avatar>
                          <Box>
                            <Typography variant="body2" fontWeight={700}>{slot.subject_name || slot.subject_name_from_table}</Typography>
                            <Typography variant="caption" color="text.secondary">
                              {slot.class_name} – Grade {slot.grade}{slot.section}
                              {slot.start_time ? ` · ${slot.start_time}–${slot.end_time || '?'}` : ''}
                            </Typography>
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  ))}
                </Box>
              )}
            </Paper>
          </Grid>

          {/* Weekly Timetable */}
          <Grid item xs={12} md={8}>
            <Paper sx={{ borderRadius: 2, overflow: 'hidden' }}>
              <Box sx={{ p: 2, bgcolor: '#1e293b', color: '#fff', display: 'flex', alignItems: 'center', gap: 1 }}>
                <MenuBookIcon />
                <Typography fontWeight={700}>Weekly Timetable</Typography>
              </Box>

              {weekTimetable.length === 0 ? (
                <Box sx={{ p: 4, textAlign: 'center' }}>
                  <MenuBookIcon sx={{ fontSize: 48, color: 'grey.300', mb: 1 }} />
                  <Typography color="text.secondary" variant="body2">No timetable assigned yet</Typography>
                </Box>
              ) : (
                <Box sx={{ p: 2, overflow: 'auto' }}>
                  <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: 0.5, minWidth: 500 }}>
                    {DAYS.map((day, di) => (
                      <Box key={day}>
                        <Box sx={{
                          p: 1, textAlign: 'center', bgcolor: di === todayDayIdx ? '#1e293b' : '#f1f5f9',
                          color: di === todayDayIdx ? '#fff' : 'text.primary', borderRadius: '4px 4px 0 0', mb: 0.5
                        }}>
                          <Typography variant="caption" fontWeight={700}>{day.slice(0, 3)}</Typography>
                        </Box>
                        {weekTimetable
                          .filter(s => s.day_of_week === di)
                          .sort((a, b) => a.period_number - b.period_number)
                          .map(slot => (
                            <Box key={slot.id} sx={{
                              p: 0.75, mb: 0.5, borderRadius: 1,
                              bgcolor: PERIOD_COLORS[(slot.period_number - 1) % PERIOD_COLORS.length] + '22',
                              borderLeft: `3px solid ${PERIOD_COLORS[(slot.period_number - 1) % PERIOD_COLORS.length]}`,
                            }}>
                              <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', fontSize: '0.65rem' }}>
                                P{slot.period_number} · {slot.subject_name || slot.subject_name_from_table}
                              </Typography>
                              <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.6rem', display: 'block' }}>
                                {slot.class_name} {slot.grade}{slot.section}
                              </Typography>
                            </Box>
                          ))}
                        {weekTimetable.filter(s => s.day_of_week === di).length === 0 && (
                          <Box sx={{ p: 1, textAlign: 'center' }}>
                            <Typography variant="caption" color="grey.400" sx={{ fontSize: '0.6rem' }}>–</Typography>
                          </Box>
                        )}
                      </Box>
                    ))}
                  </Box>
                </Box>
              )}
            </Paper>
          </Grid>

          {/* School Calendar */}
          <Grid item xs={12}>
            <Paper sx={{ borderRadius: 2, overflow: 'hidden' }}>
              <Box sx={{ p: 2, bgcolor: '#1e293b', color: '#fff', display: 'flex', alignItems: 'center', gap: 1 }}>
                <CalendarTodayIcon />
                <Typography fontWeight={700}>School Calendar</Typography>
                <Box sx={{ ml: 'auto', display: 'flex', gap: 1 }}>
                  {[{ label: 'Holiday', color: '#ef4444' }, { label: 'Event', color: '#1976d2' }].map(l => (
                    <Chip key={l.label} label={l.label} size="small" sx={{ bgcolor: l.color, color: '#fff', fontSize: '0.65rem' }} />
                  ))}
                </Box>
              </Box>
              <Box sx={{ p: 2, height: 450, '& .rbc-calendar': { fontFamily: 'inherit' } }}>
                <Calendar
                  localizer={localizer}
                  events={calEvents}
                  date={calDate}
                  view={view}
                  onView={setView}
                  onNavigate={setCalDate}
                  eventPropGetter={eventStyleGetter}
                  popup
                  style={{ height: '100%' }}
                />
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </Box>
  );
}
